import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { TRequestStatus } from "@models/msc.model";
import rolesService from "@/services/roles.service";
import { TPermissionsCatalog, TRole } from "@/models/roles.model";
import { useUsersStore } from "@/store/users/users.store";

type RolesState = {
  initialized: boolean;
  status: TRequestStatus;
  roles: TRole[];
  permissionsCatalog: TPermissionsCatalog | null;
  catalogInitialized: boolean;
};

type RolesActions = {
  getAllRoles: () => Promise<TRole[]>;
  saveRole: (data: TRole) => Promise<TRole>;
  updateRole: (data: TRole) => Promise<void>;
  removeRole: (roleId: number) => Promise<void>;
  getPermissionsCatalog: () => Promise<TPermissionsCatalog>;
  syncRoleUsers: (roleId: number, selectedUserIds: number[]) => Promise<void>;
};

export const useRolesStore = create<RolesState & RolesActions>()(
  devtools(
    (set, get) => ({
      initialized: false,
      status: "IDLE",
      roles: [],
      permissionsCatalog: null,
      catalogInitialized: false,

      getAllRoles: async () => {
        const { roles, initialized } = get();
        if (initialized) return roles;

        set({ status: "PENDING" }, false, "getAllRoles/pending");
        const response = await rolesService.getAll();
        set(
          { roles: response, status: "IDLE", initialized: true },
          false,
          "getAllRoles/fulfilled"
        );
        return response;
      },

      saveRole: async (data) => {
        const response = await rolesService.save(data);
        set((state) => ({ roles: [response, ...state.roles] }), false, "saveRole/fulfilled");
        return response;
      },

      updateRole: async (data) => {
        await rolesService.update(data);
        set(
          (state) => ({ roles: state.roles.map((t) => (t.id === data.id ? data : t)) }),
          false,
          "updateRole/fulfilled"
        );
      },

      // Optimistic removal: drop the role immediately, restore on API failure.
      removeRole: async (roleId) => {
        const rolesForRollback = get().roles;
        set(
          (state) => ({ roles: state.roles.filter((t) => t.id !== roleId) }),
          false,
          "removeRole/optimistic"
        );

        try {
          await rolesService.removeById(roleId);
        } catch (error) {
          set({ roles: rolesForRollback, status: "IDLE" }, false, "removeRole/rollback");
          throw error;
        }
      },

      getPermissionsCatalog: async () => {
        const { catalogInitialized, permissionsCatalog } = get();
        if (catalogInitialized && permissionsCatalog) return permissionsCatalog;

        const response = await rolesService.getPermissionsCatalog();
        set(
          { permissionsCatalog: response, catalogInitialized: true },
          false,
          "getPermissionsCatalog/fulfilled"
        );
        return response;
      },

      /**
       * Replace the full set of users assigned to a role via `PUT /roles/{roleId}/users`.
       * After the request succeeds, reflect the change in the users store:
       *   - Users in `selectedUserIds` get the role added to their `roles` array (if missing).
       *   - Users previously in the role but not in `selectedUserIds` get the role removed.
       */
      syncRoleUsers: async (roleId, selectedUserIds) => {
        await rolesService.assignUsers(roleId, selectedUserIds);

        const { users, userRolesUpdated } = useUsersStore.getState();
        const selectedSet = new Set(selectedUserIds);

        users.forEach((user) => {
          const hadRole = user.roles?.includes(roleId) ?? false;
          const shouldHaveRole = selectedSet.has(user.id);
          if (hadRole === shouldHaveRole) return;

          const currentRoles = user.roles || [];
          const nextRoles = shouldHaveRole
            ? Array.from(new Set([...currentRoles, roleId]))
            : currentRoles.filter((id) => id !== roleId);

          userRolesUpdated({ id: user.id, roles: nextRoles });
        });
      },
    }),
    { name: "roles" }
  )
);
